// Selection manager for raycasting and object selection
// Handles hover preview (cursor change) and click-to-select

import * as THREE from 'three'
import type { Plant, Building, GroundShape, BoundaryObject, Zone, ContextPin } from '@/types'
import { debug } from '@/utils/debug'

export type SelectableObject = Plant | Building | GroundShape | BoundaryObject | Zone | ContextPin

export interface SelectionResult {
  object: SelectableObject
  mesh: THREE.Object3D
}

export interface MultiSelectDelta { dx: number; dy: number; dRot: number }

export type SelectionCallback = (result: SelectionResult | null) => void
export type HoverCallback = (result: SelectionResult | null) => void

export class SelectionManager {
  private raycaster: THREE.Raycaster
  private mouse: THREE.Vector2
  private getCamera: () => THREE.Camera
  private selectableGroups: THREE.Group[]
  private domElement: HTMLElement

  private selectedResult: SelectionResult | null = null
  private hoveredResult: SelectionResult | null = null
  private enabled: boolean = true
  private activeLayer: string | null = null

  private selectionCallbacks: SelectionCallback[] = []
  private hoverCallbacks: HoverCallback[] = []
  private multiSelectCallbacks: ((results: SelectionResult[] | null) => void)[] = []
  private multiSelectDeltaCallbacks: ((delta: MultiSelectDelta) => void)[] = []
  private multiSelectApplyHandler: ((dx: number, dy: number, dRot: number) => void) | null = null

  constructor(
    getCamera: () => THREE.Camera,
    groups: THREE.Group[],
    domElement: HTMLElement
  ) {
    this.raycaster = new THREE.Raycaster()
    this.mouse = new THREE.Vector2()
    this.getCamera = getCamera
    this.selectableGroups = groups
    this.domElement = domElement

    debug.log('Selection', 'SelectionManager initialized')
  }

  /**
   * Add a group to the selectable groups
   */
  addSelectableGroup(group: THREE.Group): void {
    if (!this.selectableGroups.includes(group)) {
      this.selectableGroups.push(group)
      debug.log('Selection', `Added selectable group: ${group.name}`)
    }
  }

  /**
   * Remove a group from the selectable groups
   */
  removeSelectableGroup(group: THREE.Group): void {
    const index = this.selectableGroups.indexOf(group)
    if (index >= 0) {
      this.selectableGroups.splice(index, 1)
      debug.log('Selection', `Removed selectable group: ${group.name}`)
    }
  }

  /**
   * Set the active layer for selection filtering.
   * Only objects on the active layer can be selected/hovered.
   * null = no filtering (all layers selectable).
   */
  setActiveLayer(layer: string | null): void {
    this.activeLayer = layer
    // Deselect if current selection is on a different layer
    if (this.selectedResult && layer !== null) {
      const selectedLayer = this.getObjectLayer(this.selectedResult.object)
      if (selectedLayer !== layer) {
        this.deselect()
      }
    }
    // Clear hover if on a different layer
    if (this.hoveredResult && layer !== null) {
      const hoveredLayer = this.getObjectLayer(this.hoveredResult.object)
      if (hoveredLayer !== layer) {
        this.clearHover()
      }
    }
    debug.log('Selection', `Active layer: ${layer ?? 'all'}`)
  }

  /**
   * Enable or disable selection
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled
    if (!enabled) {
      this.domElement.style.cursor = ''
    }
    debug.log('Selection', `Selection ${enabled ? 'enabled' : 'disabled'}`)
  }

  /**
   * Check if selection is enabled
   */
  isEnabled(): boolean {
    return this.enabled
  }

  setOnSelectionChange(callback: SelectionCallback): void {
    this.selectionCallbacks.push(callback)
  }

  setOnHoverChange(callback: HoverCallback): void {
    this.hoverCallbacks.push(callback)
  }

  /**
   * Handle mouse move for hover preview
   */
  handleMouseMove(event: MouseEvent): void {
    if (!this.enabled) return

    const hit = this.raycastAt(event)

    // Compare by object reference (not the whole result)
    const currentObject = this.hoveredResult?.object ?? null
    const hitObject = hit?.object ?? null

    if (hitObject !== currentObject) {
      this.hoveredResult = hit

      // Update cursor
      this.domElement.style.cursor = hit ? 'pointer' : ''

      // Fire hover callbacks
      this.hoverCallbacks.forEach(cb => cb(hit))

      if (hit) {
        debug.log('Selection', `Hover: ${hit.object.type} ${hit.object.id}`)
      }
    }
  }

  /**
   * Handle click for selection
   */
  handleClick(event: MouseEvent): void {
    if (!this.enabled) return

    const hit = this.raycastAt(event)

    if (hit) {
      this.selectResult(hit)
    } else {
      this.deselect()
    }
  }

  /**
   * Raycast at mouse position and return hit result with object and mesh.
   * Iterates through all intersections to skip objects on locked layers.
   */
  private raycastAt(event: MouseEvent): SelectionResult | null {
    const rect = this.domElement.getBoundingClientRect()
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1

    this.raycaster.setFromCamera(this.mouse, this.getCamera())

    // Only raycast against visible groups
    const visibleGroups = this.selectableGroups.filter(g => g.visible)
    const intersects = this.raycaster.intersectObjects(visibleGroups, true)

    for (const intersect of intersects) {
      const result = this.findSelectableParent(intersect.object)
      if (!result) continue
      // Layer filter: if activeLayer is set, only allow matching layer
      if (this.activeLayer !== null && this.getObjectLayer(result.object) !== this.activeLayer) continue
      return result
    }

    return null
  }

  /**
   * Determine which layer a selectable object belongs to.
   */
  getObjectLayer(object: SelectableObject): string {
    if ('zoneType' in object) return 'metadata'
    if ('pinType' in object) return 'metadata'
    if ('type' in object) {
      const type = (object as { type: string }).type
      if (type === 'plant') return 'plants'
      if (type === 'building') return 'buildings'
      if (type === 'rectangle' || type === 'polygon' || type === 'ellipse' || type === 'path') return 'ground'
      if (type === 'boundary') return 'map'
    }
    return 'unknown'
  }

  /**
   * Walk up the object tree to find the selectable parent with userData
   * Returns both the data object and the mesh for bounding box calculations
   */
  private findSelectableParent(object: THREE.Object3D): SelectionResult | null {
    let current: THREE.Object3D | null = object
    while (current) {
      const data = current.userData
      if (data.plantData) {
        // If this is a top-view billboard with a main mesh reference, use that for highlighting
        const mesh = data.mainMesh || current
        return { object: data.plantData as Plant, mesh }
      }
      if (data.buildingData) return { object: data.buildingData as Building, mesh: current }
      if (data.shapeData) return { object: data.shapeData as GroundShape, mesh: current }
      if (data.boundaryData) return { object: data.boundaryData as BoundaryObject, mesh: current }
      if (data.zoneData) return { object: data.zoneData as Zone, mesh: current }
      if (data.pinData) return { object: data.pinData as ContextPin, mesh: current }
      current = current.parent
    }
    return null
  }

  /**
   * Select an object (internal use - accepts SelectionResult)
   */
  private selectResult(result: SelectionResult): void {
    if (result.object !== this.selectedResult?.object) {
      this.selectedResult = result
      this.selectionCallbacks.forEach(cb => cb(result))
      debug.log('Selection', `Selected: ${result.object.type} ${result.object.id}`)
    }
  }

  /**
   * Programmatically select an object by finding its mesh in the scene
   */
  select(object: SelectableObject): void {
    // Find the mesh that has this object in its userData
    const mesh = this.findMeshForObject(object)
    if (mesh) {
      this.selectResult({ object, mesh })
    } else {
      debug.warn('Selection', `Could not find mesh for object: ${object.type} ${object.id}`)
    }
  }

  /**
   * Find the mesh that contains the given object in its userData
   */
  private findMeshForObject(object: SelectableObject): THREE.Object3D | null {
    for (const group of this.selectableGroups) {
      let foundMesh: THREE.Object3D | null = null
      group.traverse((child) => {
        if (foundMesh) return // Already found
        const data = child.userData
        if (
          data.plantData === object ||
          data.buildingData === object ||
          data.shapeData === object ||
          data.boundaryData === object ||
          data.zoneData === object ||
          data.pinData === object
        ) {
          foundMesh = child
        }
      })
      if (foundMesh) return foundMesh
    }
    return null
  }

  /**
   * Deselect current object
   */
  deselect(): void {
    if (this.selectedResult) {
      this.selectedResult = null
      this.selectionCallbacks.forEach(cb => cb(null))
      debug.log('Selection', 'Deselected')
    }
  }

  /**
   * Get currently selected object
   */
  getSelected(): SelectableObject | null {
    return this.selectedResult?.object ?? null
  }

  /**
   * Get currently selected result (with mesh)
   */
  getSelectedResult(): SelectionResult | null {
    return this.selectedResult
  }

  /**
   * Get currently hovered object
   */
  getHovered(): SelectableObject | null {
    return this.hoveredResult?.object ?? null
  }

  /**
   * Get currently hovered result (with mesh)
   */
  getHoveredResult(): SelectionResult | null {
    return this.hoveredResult
  }

  /**
   * Update the mesh reference for the currently selected object.
   * Called when a renderer replaces a mesh (e.g. building remove+add).
   */
  updateSelectedMesh(objectId: string, newMesh: THREE.Object3D): void {
    if (this.selectedResult && this.selectedResult.object.id === objectId) {
      // Also refresh the object data from the new mesh's userData
      const newResult = this.findSelectableParent(newMesh)
      this.selectedResult = newResult ?? { ...this.selectedResult, mesh: newMesh }
    }
  }

  /** Public raycast without side effects — used by SelectTool for Shift+click */
  raycastAtEvent(event: MouseEvent): SelectionResult | null {
    return this.raycastAt(event)
  }

  /** Expose active layer for Ctrl+A filtering */
  getActiveLayerName(): string | null {
    return this.activeLayer
  }

  /** Find the mesh for a given selectable object (public for multi-select) */
  findMeshForSelectableObject(object: SelectableObject): THREE.Object3D | null {
    return this.findMeshForObject(object)
  }

  /**
   * Find mesh by object ID (not reference equality).
   * Used by multi-select when zone/pin state updates create new object references.
   */
  findMeshById(id: string, _objectType?: string): THREE.Object3D | null {
    for (const group of this.selectableGroups) {
      let foundMesh: THREE.Object3D | null = null
      group.traverse((child) => {
        if (foundMesh) return
        const data = child.userData
        if (
          (data.plantData?.id === id) ||
          (data.buildingData?.id === id) ||
          (data.shapeData?.id === id) ||
          (data.boundaryData?.id === id) ||
          (data.zoneData?.id === id) ||
          (data.pinData?.id === id)
        ) {
          foundMesh = child
        }
      })
      if (foundMesh) return foundMesh
    }
    return null
  }

  /** Subscribe to multi-selection changes */
  setOnMultiSelectionChange(cb: (results: SelectionResult[] | null) => void): void {
    this.multiSelectCallbacks.push(cb)
  }

  /** Notify multi-selection change listeners */
  notifyMultiSelection(results: SelectionResult[] | null): void {
    for (const cb of this.multiSelectCallbacks) cb(results)
  }

  /** Subscribe to live multi-select delta updates (forward: tool → panel) */
  setOnMultiSelectDelta(cb: (delta: MultiSelectDelta) => void): void {
    this.multiSelectDeltaCallbacks.push(cb)
  }

  /** Fire live delta from SelectTool during group drag/rotate */
  notifyMultiSelectDelta(delta: MultiSelectDelta): void {
    for (const cb of this.multiSelectDeltaCallbacks) cb(delta)
  }

  /** Register handler for typed delta from panel → tool */
  setMultiSelectApplyHandler(handler: ((dx: number, dy: number, dRot: number) => void) | null): void {
    this.multiSelectApplyHandler = handler
  }

  /** Apply typed delta values from panel input fields */
  applyMultiSelectDelta(dx: number, dy: number, dRot: number): void {
    this.multiSelectApplyHandler?.(dx, dy, dRot)
  }

  /**
   * Clear hover state (e.g., when mouse leaves canvas)
   */
  clearHover(): void {
    if (this.hoveredResult) {
      this.hoveredResult = null
      this.domElement.style.cursor = ''
      this.hoverCallbacks.forEach(cb => cb(null))
    }
  }
}
